import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useContentAwareTimelineDiffCasablanca } from '../hooks/TownsContext/useContentAwareTimelineDiff';
import { useBlockedUsers } from '../hooks/use-blocked-users';
import { useSpacesIds } from '../hooks/TownsContext/useSpaceIds';
import { useSpaceUnreads } from '../hooks/TownsContext/useSpaceUnreads';
import { useSpaces } from '../hooks/TownsContext/useSpaces';
import { useTownsClientListener } from '../hooks/use-towns-client-listener';
import { QueryProvider } from './QueryProvider';
import { useCasablancaTimelines } from '../hooks/TownsContext/useCasablancaTimelines';
import { useCasablancaRooms } from '../hooks/TownsContext/useCasablancaRooms';
import { useCasablancaDMs } from '../hooks/CasablancClient/useCasablancaDMs';
import { useDMUnreads } from '../hooks/TownsContext/useDMUnreads';
import { useClientInitStatus } from '../hooks/TownsContext/useClientInitStatus';
import { makeProviderFromChain, makeProviderFromConfig } from '../utils/provider-utils';
import { useUserLookupUpdater } from '../hooks/use-user-lookup-updater';
import { useStreamMetadataUpdater } from '../hooks/use-stream-metadata-updater';
import { NotificationSettingsClient } from '../client/TownsNotifciationSettings';
import { useSpaceRollups } from '../hooks/use-space-data';
import { useCalculateSpaceThreadRoots } from '../hooks/use-space-thread-roots';
import { useCalculateSpaceMentions } from '../hooks/use-space-mentions';
import { dlogger } from '@towns-protocol/dlog';
import { getXchainConfig } from '../client/XChainConfig';
const log = dlogger('towns:context');
export const TownsContext = createContext(undefined);
/**
 * use instead of React.useContext, throws if not in a Provider
 */
export function useTownsContext() {
    const context = useContext(TownsContext);
    if (!context) {
        throw new Error('useTownsContext must be used in a TownsContextProvider');
    }
    return context;
}
export function TownsContextProvider({ QueryClientProvider = QueryProvider, ...props }) {
    return (React.createElement(QueryClientProvider, null,
        React.createElement(TownsContextImplMemo, { ...props })));
}
const TownsContextImpl = (props) => {
    const { environmentId, baseConfig, baseChain, riverConfig, riverChain, timelineFilter, streamMetadataUrl, } = props;
    const previousProps = useRef();
    useEffect(() => {
        if (previousProps.current) {
            Object.keys(previousProps.current).forEach((key, i) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
                if (previousProps.current[key] !== props[key]) {
                    log.info('prop changed', i, key);
                }
            });
        }
    });
    previousProps.current = props;
    const baseProvider = useMemo(() => {
        return makeProviderFromChain(baseChain);
    }, [baseChain]);
    const riverProvider = useMemo(() => {
        return makeProviderFromConfig(riverChain);
    }, [riverChain]);
    const xchainConfig = useMemo(() => {
        return getXchainConfig(baseChain.id, props.supportedXChainRpcMapping ?? {});
    }, [baseChain.id, props.supportedXChainRpcMapping]);
    const townsOpts = useMemo(() => {
        return {
            environmentId,
            baseChainId: baseChain.id,
            baseConfig,
            baseProvider,
            riverChainId: riverChain.chainId,
            riverConfig,
            riverProvider,
            xchainConfig,
            accountAbstractionConfig: props.accountAbstractionConfig,
            highPriorityStreamIds: props.highPriorityStreamIds,
            unpackEnvelopeOpts: props.unpackEnvelopeOpts,
            ethMainnetRpcUrl: props.ethMainnetRpcUrl,
            analytics: props.analytics,
            createLegacySpaces: props.createLegacySpaces,
            useModifySync: props.useModifySync,
        };
    }, [
        environmentId,
        baseChain.id,
        baseConfig,
        baseProvider,
        riverChain.chainId,
        riverConfig,
        riverProvider,
        xchainConfig,
        props.accountAbstractionConfig,
        props.highPriorityStreamIds,
        props.unpackEnvelopeOpts,
        props.ethMainnetRpcUrl,
        props.analytics,
        props.createLegacySpaces,
        props.useModifySync,
    ]);
    const { client, clientSingleton, casablancaClient, signerContext } = useTownsClientListener(townsOpts);
    const notificationSettingsClient = useNotificationSettingsClient(signerContext, environmentId, props.riverNotificationServiceUrl);
    const spaceIds = useSpacesIds(casablancaClient);
    useContentAwareTimelineDiffCasablanca(casablancaClient);
    const clientStatus = useClientInitStatus(casablancaClient);
    const { spaces } = useSpaces(townsOpts, spaceIds, casablancaClient);
    const { channels: dmChannels } = useCasablancaDMs(casablancaClient);
    const blockedUserIds = useBlockedUsers(casablancaClient);
    useUserLookupUpdater(townsOpts, casablancaClient, client);
    useStreamMetadataUpdater(casablancaClient, client, streamMetadataUrl);
    useCalculateSpaceThreadRoots(townsOpts);
    useCalculateSpaceMentions(townsOpts);
    const { spaceUnreads, spaceMentions, spaceUnreadChannelIds } = useSpaceUnreads(client, notificationSettingsClient);
    const { dmUnreadChannelIds } = useDMUnreads(casablancaClient, dmChannels);
    const rooms = useCasablancaRooms(townsOpts, casablancaClient);
    useCasablancaTimelines(casablancaClient, timelineFilter);
    useSpaceRollups(townsOpts, casablancaClient, baseProvider);
    const value = useMemo(() => {
        const newValue = {
            environmentId,
            client,
            clientSingleton,
            casablancaClient,
            notificationSettingsClient,
            baseConfig,
            baseChain,
            baseProvider,
            riverConfig,
            riverChain,
            riverProvider,
            rooms,
            spaceUnreads,
            spaceMentions,
            spaceUnreadChannelIds,
            spaces,
            spaceIds,
            dmChannels,
            dmUnreadChannelIds,
            clientStatus,
            blockedUserIds,
            signerContext,
            xchainConfig,
        };
        return newValue;
    }, [
        environmentId,
        client,
        clientSingleton,
        casablancaClient,
        notificationSettingsClient,
        baseConfig,
        baseChain,
        baseProvider,
        riverConfig,
        riverChain,
        riverProvider,
        rooms,
        spaceUnreads,
        spaceMentions,
        spaceUnreadChannelIds,
        spaces,
        spaceIds,
        dmChannels,
        dmUnreadChannelIds,
        clientStatus,
        blockedUserIds,
        signerContext,
        xchainConfig,
    ]);
    const valueRef = useRef();
    useEffect(() => {
        //log.info('context impl changed', !!value)
        if (valueRef.current) {
            // const diffKeys = Object.keys(valueRef.current).reduce((acc, key) => {
            //     // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
            //     if ((valueRef.current as any)[key] !== (value as any)[key]) {
            //         acc.push(key)
            //     }
            //     return acc
            // }, [] as string[])
            // log.info('new values', diffKeys)
        }
        valueRef.current = value;
    }, [value]);
    return React.createElement(TownsContext.Provider, { value: value }, props.children);
};
/// the towns client needs to be nested inside a Web3 provider, hence the need for this component
const TownsContextImplMemo = React.memo(TownsContextImpl, (prevProps, nextProps) => {
    let result = true;
    Object.keys(prevProps).forEach((key, i) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        if (prevProps[key] !== nextProps[key]) {
            log.info('context impl props changed', i, key);
            result = false;
        }
    });
    return result;
});
// private singleton, not exported, to use the notification settings client get it from useTownsContext()
function useNotificationSettingsClient(signerContext, environmentId, url) {
    return useMemo(() => {
        if (!signerContext) {
            return undefined;
        }
        return new NotificationSettingsClient(signerContext, environmentId, url);
    }, [environmentId, signerContext, url]);
}
//# sourceMappingURL=TownsContextProvider.js.map